import React, { useEffect, useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import { useAmbiance } from '../Ambiance/AmbianceContext';
import { Eyebrow } from './VitrineUi';
import { BlocFormules, TITRE_SECTION } from './ModulesEtFormules';

interface FormulePublique {
    id: string; nom: string; description: string; prix_eur: number; type_client: string; sms_inclus: number; salaries_inclus: number;
    modules: { name: string; label: string; statut: string }[];
}

const LIBELLES_TYPE: Record<string, string> = { restauration: 'Restauration', hotellerie: 'Hôtellerie', prestation: 'Prestation de service', fournisseur: 'Fournisseur' };
const ORDRE_TYPES = ['restauration', 'hotellerie', 'prestation', 'fournisseur'];
const prix = (n: number) => `${Number.isInteger(n) ? n : n.toFixed(2).replace('.', ',')} €`;

/**
 * Les formules telles que le super admin les a réglées : un onglet par type de client, chaque type a ses prix.
 * Lues sur /api/public/formules ; si la lecture échoue, on retombe sur les formules de départ de la restauration.
 */
export const BlocFormulesParType: React.FC = () => {
    const { tokens } = useAmbiance();
    const [formules, setFormules] = useState<FormulePublique[] | null>(null);
    const [type, setType] = useState('restauration');

    useEffect(() => {
        let vivant = true;
        fetch('/api/public/formules').then((r) => (r.ok ? r.json() : Promise.reject())).then((j) => { if (vivant) setFormules(Array.isArray(j?.formules) ? j.formules : []); }).catch(() => { if (vivant) setFormules([]); });
        return () => { vivant = false; };
    }, []);

    const types = useMemo(() => ORDRE_TYPES.filter((t) => (formules || []).some((f) => f.type_client === t)), [formules]);
    useEffect(() => { if (types.length && !types.includes(type)) setType(types[0]); }, [types, type]);
    const liste = useMemo(() => (formules || []).filter((f) => f.type_client === type), [formules, type]);
    const lignes = useMemo(() => {
        const vus = new Map<string, { label: string; statut: string }>();
        for (const f of liste) for (const m of f.modules) if (!vus.has(m.name)) vus.set(m.name, { label: m.label, statut: m.statut });
        return [...vus.entries()].map(([name, v]) => ({ name, ...v }));
    }, [liste]);

    if (formules === null || formules.length === 0) return <BlocFormules />;
    const milieu = liste.length > 2 ? Math.floor(liste.length / 2) : -1;

    return (
        <section id="formules" className="bg-surface-app border-y border-border scroll-mt-32">
            <div className="max-w-[1160px] mx-auto px-5 py-16 md:py-20">
                <Eyebrow>Formules</Eyebrow>
                <h2 className={`max-w-[640px] ${TITRE_SECTION} mb-3`}>Un prix par mois, selon votre métier.</h2>
                <p className="max-w-[580px] text-[14.5px] text-content-muted mb-8">Chaque type d'établissement a ses formules et ses prix. Vous pouvez aussi ajouter des modules à la carte.</p>

                {types.length > 1 && (
                    <div className="flex flex-wrap gap-2 mb-8" role="tablist" aria-label="Type d'établissement">
                        {types.map((t) => (
                            <button key={t} type="button" role="tab" aria-selected={type === t} onClick={() => setType(t)}
                                className={`h-[42px] px-5 rounded-full text-[14px] font-semibold border transition-colors ${type === t ? 'bg-content text-surface border-content' : 'bg-surface text-content border-border hover:border-content'}`}>
                                {LIBELLES_TYPE[t] || t}
                            </button>
                        ))}
                    </div>
                )}

                <div className={`grid grid-cols-1 gap-4 mb-10 ${liste.length >= 3 ? 'md:grid-cols-3' : liste.length === 2 ? 'md:grid-cols-2' : ''}`}>
                    {liste.map((f, i) => (
                        <div key={f.id} className="rounded-2xl border bg-surface p-6 relative" style={{ borderColor: i === milieu ? tokens.acc : 'var(--border)', borderWidth: i === milieu ? 2 : 1 }}>
                            {i === milieu && <span className="absolute -top-3 left-6 rounded-full px-3 py-0.5 text-[11px] font-bold text-white" style={{ background: tokens.acc }}>La plus choisie</span>}
                            <div className="text-[15px] font-semibold">{f.nom}</div>
                            <div className="mt-2 mb-2"><span className="text-[40px] font-bold leading-none tracking-tight">{prix(f.prix_eur)}</span> <span className="text-[13px] text-content-muted">/ mois</span></div>
                            {f.description && <p className="text-[13.5px] text-content-muted mb-3">{f.description}</p>}
                            <ul className="text-[12.5px] text-content-muted space-y-1">
                                {f.salaries_inclus > 0 && <li>{f.salaries_inclus} salariés inclus</li>}
                                {f.sms_inclus > 0 && <li>{f.sms_inclus} SMS inclus par mois</li>}
                            </ul>
                        </div>
                    ))}
                </div>

                {lignes.length > 0 && (
                    <div className="rounded-2xl border border-border bg-surface overflow-x-auto">
                        <table className="w-full min-w-[560px] text-left text-[13.5px]">
                            <caption className="sr-only">Ce que comprend chaque formule</caption>
                            <thead>
                                <tr className="border-b border-border">
                                    <th scope="col" className="py-3 px-4 font-semibold">Ce qui est inclus</th>
                                    {liste.map((f) => <th scope="col" key={f.id} className="py-3 px-3 text-center font-semibold w-[110px]">{f.nom}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {lignes.map((l) => (
                                    <tr key={l.name} className="border-b border-border last:border-0">
                                        <th scope="row" className="py-3 px-4 font-medium">
                                            {l.label}
                                            {l.statut !== 'disponible' && <span className="ml-2 text-[10px] font-bold uppercase tracking-[.09em] text-content-muted">Bientôt</span>}
                                        </th>
                                        {liste.map((f) => (
                                            <td key={f.id} className="py-3 px-3 text-center">
                                                {f.modules.some((m) => m.name === l.name)
                                                    ? <Check size={17} className="inline" style={{ color: tokens.acc }} aria-label="Inclus" />
                                                    : <span className="text-content-muted" aria-label="Non inclus">—</span>}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <p className="mt-4 text-[12.5px] text-content-muted">Les modules marqués « Bientôt » sont en construction et ne sont pas encore disponibles.</p>
            </div>
        </section>
    );
};
